// Assets and maintenance (docs/plans/phase3-operations-modules.md §3.2). URLs carry internal
// IDs and filters only.
import { queryOptions } from '@tanstack/react-query';
import {
  assetListSchema,
  assetOptionsSchema,
  assetSchema,
  operationsListItemSchema,
  partOptionsSchema,
  workOrderListSchema,
  workOrderSchema,
  type AddWorkOrderPartRequest,
  type ChangeAssetStatusRequest,
  type OpenWorkOrderRequest,
  type RegisterAssetRequest,
  type SaveOperationsListRequest,
  type UpdateAssetRequest,
} from '@wodi/shared';
import { z } from 'zod';

import { api } from './client.ts';

export type AssetFilter = 'in_service' | 'out_of_service' | 'condemned' | 'all';
export type WorkOrderFilter = 'open' | 'closed' | 'all';

export const assetKeys = {
  all: ['assets'] as const,
  options: ['assets', 'options'] as const,
  list: (filter: AssetFilter) => ['assets', 'list', filter] as const,
  due: ['assets', 'due'] as const,
  one: (id: string) => ['assets', 'one', id] as const,
  workOrders: (filter: WorkOrderFilter) => ['assets', 'work-orders', filter] as const,
  workOrder: (id: string) => ['assets', 'work-order', id] as const,
  parts: (storeId: string) => ['assets', 'parts', storeId] as const,
};

export function fetchAssetOptions() {
  return api('GET', '/assets/options', { schema: assetOptionsSchema });
}

export const assetOptionsQuery = queryOptions({
  queryKey: assetKeys.options,
  queryFn: fetchAssetOptions,
});

export function assetListQuery(filter: AssetFilter) {
  return queryOptions({
    queryKey: assetKeys.list(filter),
    queryFn: () => api('GET', `/assets?status=${filter}`, { schema: assetListSchema }),
  });
}

export const maintenanceDueQuery = queryOptions({
  queryKey: assetKeys.due,
  queryFn: () => api('GET', '/assets/maintenance-due', { schema: assetListSchema }),
});

export function assetQuery(id: string) {
  return queryOptions({
    queryKey: assetKeys.one(id),
    queryFn: () => api('GET', `/assets/${id}`, { schema: assetSchema }),
  });
}

export function workOrderListQuery(filter: WorkOrderFilter) {
  return queryOptions({
    queryKey: assetKeys.workOrders(filter),
    queryFn: () => api('GET', `/work-orders?status=${filter}`, { schema: workOrderListSchema }),
  });
}

export function workOrderQuery(id: string) {
  return queryOptions({
    queryKey: assetKeys.workOrder(id),
    queryFn: () => api('GET', `/work-orders/${id}`, { schema: workOrderSchema }),
  });
}

export function partOptionsQuery(storeId: string) {
  return queryOptions({
    queryKey: assetKeys.parts(storeId),
    queryFn: () =>
      api('GET', `/work-orders/part-options?storeId=${storeId}`, { schema: partOptionsSchema }),
  });
}

export const registerAsset = (body: RegisterAssetRequest) =>
  api('POST', '/assets', { body, schema: assetSchema });
export const updateAsset = (id: string, body: UpdateAssetRequest) =>
  api('PUT', `/assets/${id}`, { body, schema: assetSchema });
export const changeAssetStatus = (id: string, body: ChangeAssetStatusRequest) =>
  api('POST', `/assets/${id}/status`, { body, schema: assetSchema });
export const openWorkOrder = (assetId: string, body: OpenWorkOrderRequest) =>
  api('POST', `/assets/${assetId}/work-orders`, { body, schema: workOrderSchema });

export const workOrderSteps = {
  start: (id: string) => api('POST', `/work-orders/${id}/start`, { schema: workOrderSchema }),
  complete: (id: string, workDone: string, returnToService: boolean) =>
    api('POST', `/work-orders/${id}/complete`, {
      body: { workDone, returnToService },
      schema: workOrderSchema,
    }),
  cancel: (id: string, reason: string) =>
    api('POST', `/work-orders/${id}/cancel`, { body: { reason }, schema: workOrderSchema }),
  addPart: (id: string, body: AddWorkOrderPartRequest) =>
    api('POST', `/work-orders/${id}/parts`, { body, schema: workOrderSchema }),
};

export function saveAssetCategories(body: SaveOperationsListRequest) {
  return api('PUT', '/assets/options/asset_category', {
    body,
    schema: z.array(operationsListItemSchema),
  });
}
